import { useCallback, useId, useRef, type JSX } from 'react';
import type { ListEntry, PropertyDescriptor } from '../../../core/definitions';
import type { SettingsBag } from '../../../core/model';
import { DateTimeField } from './DateTimeField';
import { DevicePicker } from './DeviceField';
import { DurationField } from './DurationField';
import { groupSections, readValue, visibleDescriptors } from './coerce';
import { AssetField, BooleanField, ColorField, FontField, GuestField, NumberProp, SelectField, TextField } from './fields';
import { ListField } from './ListField';
import './properties.css';

export interface PropertyFormProps {
  descriptors: readonly PropertyDescriptor[];
  settings: SettingsBag;
  onChange(key: string, value: unknown): void;
  onCommit(): void;
  /** Property buttons; omitted where there is no runtime to invoke (filters, transitions). */
  onAction?(action: string): void;
  /** The runtime's live facts, for `info` properties with a `liveKey`. */
  live?: Readonly<Record<string, string>>;
  /** Called with true while any field holds a value it refuses to apply. */
  onValidityChange?(invalid: boolean): void;
}

type FieldProps = { desc: PropertyDescriptor; id: string; onInvalid(key: string, problem: string | null): void } & Omit<
  PropertyFormProps,
  'descriptors' | 'onValidityChange'
>;

function Field(props: FieldProps): JSX.Element {
  const { desc, id, settings, onCommit, onAction, live } = props;
  const value = readValue(desc, settings);
  const onChange = (next: unknown): void => props.onChange(desc.key, next);
  const onInvalid = props.onInvalid;
  const reportInvalid = useCallback((problem: string | null) => onInvalid(desc.key, problem), [onInvalid, desc.key]);
  switch (desc.type) {
    case 'text':
      return <TextField desc={desc} id={id} value={value as string} onChange={onChange} onCommit={onCommit} onInvalid={reportInvalid} />;
    case 'number':
      return <NumberProp desc={desc} id={id} value={value as number} onChange={onChange} onCommit={onCommit} />;
    case 'boolean':
      return <BooleanField desc={desc} id={id} value={value as boolean} onChange={onChange} onCommit={onCommit} />;
    case 'select':
      return <SelectField desc={desc} id={id} value={value as string | number} onChange={onChange} onCommit={onCommit} />;
    case 'color':
      return <ColorField desc={desc} id={id} value={value as string} onChange={onChange} onCommit={onCommit} />;
    case 'asset':
      return <AssetField desc={desc} id={id} value={value as string | string[]} onChange={onChange} onCommit={onCommit} />;
    case 'device':
      return (
        <DevicePicker
          id={id}
          kind={desc.deviceKind}
          label={desc.label}
          value={value as string}
          pairedVideoDeviceId={desc.pairWith ? String(settings[desc.pairWith] ?? '') : undefined}
          onChange={(deviceId) => {
            onChange(deviceId);
            onCommit();
          }}
        />
      );
    case 'font':
      return <FontField desc={desc} id={id} value={value as string} onChange={onChange} onCommit={onCommit} />;
    case 'guest':
      return <GuestField desc={desc} id={id} value={value as string} onChange={onChange} onCommit={onCommit} />;
    case 'list':
      return (
        <ListField
          desc={desc}
          id={id}
          value={value as ListEntry[]}
          onChange={onChange}
          onCommit={onCommit}
          renderFields={(entry, onEntryChange) => (
            <PropertyForm descriptors={desc.fields ?? []} settings={entry} onChange={onEntryChange} onCommit={onCommit} onAction={onAction} live={live} />
          )}
        />
      );
    case 'datetime':
      return <DateTimeField desc={desc} id={id} value={value as string} onChange={onChange} onCommit={onCommit} />;
    case 'duration':
      return <DurationField desc={desc} id={id} value={value as number} onChange={onChange} onCommit={onCommit} />;
    case 'button':
      return (
        <div>
          <button
            id={id}
            type="button"
            className={desc.variant === 'primary' ? 'ui-btn primary' : desc.variant === 'danger' ? 'ui-btn danger' : 'ui-btn'}
            disabled={!onAction}
            onClick={() => onAction?.(desc.action)}
          >
            {desc.label}
          </button>
        </div>
      );
    case 'info': {
      const text = desc.liveKey ? live?.[desc.liveKey] : undefined;
      return (
        <span className="ui-dim ui-selectable" aria-live={desc.liveKey ? 'polite' : undefined}>
          {desc.liveKey ? (text ?? '—') : (desc.description ?? '')}
        </span>
      );
    }
    default: {
      // Reached only by a plugin built against a newer descriptor set; its stored value is left untouched.
      const unknown = desc as { type?: unknown };
      return <span className="ui-warn-text">This setting (type "{String(unknown.type)}") cannot be edited in this version; its value is kept as it is.</span>;
    }
  }
}

/** Generic settings form generated from property descriptors, grouped by `section`. */
export function PropertyForm(props: PropertyFormProps): JSX.Element {
  const prefix = useId();
  const problems = useRef(new Map<string, string>());
  const { onValidityChange } = props;
  const onInvalid = useCallback(
    (key: string, problem: string | null) => {
      const before = problems.current.size > 0;
      if (problem) problems.current.set(key, problem);
      else problems.current.delete(key);
      const after = problems.current.size > 0;
      if (before !== after) onValidityChange?.(after);
    },
    [onValidityChange],
  );
  const visible = visibleDescriptors(props.descriptors, props.settings);
  if (visible.length === 0) return <div className="ui-empty">This item has no settings.</div>;
  const row = (desc: PropertyDescriptor): JSX.Element => {
    const id = `${prefix}-${desc.key}`;
    const labelled = desc.type !== 'button';
    return (
      <div key={`${desc.type}:${desc.key}`} className="ui-field">
        {labelled ? <label htmlFor={desc.type === 'info' || desc.type === 'list' ? undefined : id}>{desc.label}</label> : <span />}
        <Field {...props} desc={desc} id={id} onInvalid={onInvalid} />
        {desc.description && desc.type !== 'info' ? <span className="ui-field-help">{desc.description}</span> : null}
      </div>
    );
  };
  return (
    <div className="property-form">
      {groupSections(visible).map((group, index) =>
        group.title ? (
          <section key={`section:${index}:${group.title}`} aria-label={group.title}>
            <div className="ui-section-title">{group.title}</div>
            {group.items.map(row)}
          </section>
        ) : (
          group.items.map(row)
        ),
      )}
    </div>
  );
}
